import { useNotifications } from '../contexts/NotificationContext'

/**
 * Custom hook for easy notification usage
 * @returns {Object} Object with notification methods
 */
export const useNotification = () => {
  const { showSuccess, showError, showWarning, showInfo, addNotification, removeNotification, clearAllNotifications } = useNotifications()

  const notify = {
    /**
     * Show success notification
     * @param {string} message - The message to display
     * @param {Object} options - Additional options
     * @param {number} options.autoHideDuration - Duration in ms before auto-hide (default: 6000)
     * @param {Object} options.anchorOrigin - Position of the snackbar
     * @returns {string} notification ID
     */
    success: (message, options = {}) => {
      return showSuccess(message, options)
    },

    /**
     * Show error notification
     * @param {string} message - The message to display
     * @param {Object} options - Additional options
     * @param {number} options.autoHideDuration - Duration in ms before auto-hide (default: 8000)
     * @param {Object} options.anchorOrigin - Position of the snackbar
     * @returns {string} notification ID
     */
    error: (message, options = {}) => {
      return showError(message, options)
    },

    /**
     * Show warning notification
     * @param {string} message - The message to display
     * @param {Object} options - Additional options
     * @param {number} options.autoHideDuration - Duration in ms before auto-hide (default: 6000)
     * @param {Object} options.anchorOrigin - Position of the snackbar
     * @returns {string} notification ID
     */
    warning: (message, options = {}) => {
      return showWarning(message, options)
    },

    /**
     * Show info notification
     * @param {string} message - The message to display
     * @param {Object} options - Additional options
     * @param {number} options.autoHideDuration - Duration in ms before auto-hide (default: 6000)
     * @param {Object} options.anchorOrigin - Position of the snackbar
     * @returns {string} notification ID
     */
    info: (message, options = {}) => {
      return showInfo(message, options)
    },

    /**
     * Add custom notification
     * @param {Object} notification - Notification object
     * @param {string} notification.type - Type of notification (success, error, warning, info)
     * @param {string} notification.message - The message to display
     * @param {number} notification.autoHideDuration - Duration in ms before auto-hide
     * @param {Object} notification.anchorOrigin - Position of the snackbar
     * @returns {string} notification ID
     */
    custom: (notification) => {
      return addNotification(notification)
    },

    /**
     * Remove specific notification
     * @param {string} id - Notification ID
     */
    remove: (id) => {
      removeNotification(id)
    },

    /**
     * Clear all notifications
     */
    clear: () => {
      clearAllNotifications()
    }
  }

  return notify
}

export default useNotification 